import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";

import SearchBoxComponent from "../../Generals/SearchBox/SearchBoxComponent";
import Button3px from "../../Generals/Buttons/3pxButton.js";
import SelectorSimple from "../../Generals/Selectors/SelectorSimple";
import NotificationModal from "../../Generals/Notifications/NotificationModal";
import FormSystemModal from "../../Generals/Modals/FormSystemModal";
import TableFrontPagination from "../../Generals/Tables/TableFrontPagination";
import IconComponent from "../../Generals/Icons/IconComponent";
import StatusChip from "../../Generals/Chips/StatusChip.js";

import Colors from "../../../assets/Variables/ColorsVariables";
import * as Styles from "./Styles.js";
import adminMethods from "../../../services/admin";
import {
  MOCK_ADMINS,
  filterMockAdmins,
  createMockAdmin,
  updateMockAdmin,
  deleteMockAdmin,
} from "./admin.mock";

const ENABLE_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
];

const SUPER_ADMIN_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Super Admin" },
  { value: "false", label: "Regular Admin" },
];

const maskPassword = (password) => {
  if (!password) return "••••••••";
  return "•".repeat(Math.max(8, String(password).length));
};

const Admin_Management_Screen = () => {
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);

  const [rowsAdmin, setRowsAdmin] = useState([]);
  const [mockAdmins, setMockAdmins] = useState(MOCK_ADMINS);

  const [anchorElAdmin, setAnchorElAdmin] = useState(null);
  const [selectedAdminRow, setSelectedAdminRow] = useState(null);

  const [filterAdmin, setFilterAdmin] = useState({
    search: "",
    enable: "",
    isSuperAdmin: "",
  });

  const [modalConfig, setModalConfig] = useState({
    open: false,
    iconType: "info",
    title: "",
    message: "",
    onAcceptLabel: "OK",
    onRefuseLabel: undefined,
    onContinueLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
    onContinue: undefined,
    onClose: () => setModalConfig((prev) => ({ ...prev, open: false })),
  });

  const [formModalConfig, setFormModalConfig] = useState({
    open: false,
    title: "",
    fields: [],
    onSubmit: null,
    onCancel: null,
    type: "form",
    confirmLabel: "Save",
    cancelLabel: "Cancel",
    onClose: () => closeFormModal(),
  });

  const adminMenuOpen = Boolean(anchorElAdmin);

  const handleOpenAdminMenu = useCallback((event, row) => {
    setAnchorElAdmin(event.currentTarget);
    setSelectedAdminRow(row);
  }, []);

  const handleCloseAdminMenu = useCallback(() => {
    setAnchorElAdmin(null);
    setSelectedAdminRow(null);
  }, []);

  const closeFormModal = () =>
    setFormModalConfig((prev) => ({ ...prev, open: false }));

  const openSuccessModal = (afterClose) =>
    setModalConfig({
      open: true,
      iconType: "success",
      title: "The operation was successful",
      message: "",
      onAcceptLabel: undefined,
      onRefuseLabel: undefined,
      onContinueLabel: "Continue",
      onAccept: undefined,
      onRefuse: undefined,
      onContinue: () => {
        if (afterClose) afterClose();
        setModalConfig((prev) => ({ ...prev, open: false }));
      },
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  const getEndpointMessage = (error, fallback = "Server error") => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.data?.message ||
      fallback
    );
  };

  const openErrorModal = (message) =>
    setModalConfig({
      open: true,
      iconType: "error",
      title: message || "Server error",
      message: "",
      onAcceptLabel: "OK",
      onRefuseLabel: undefined,
      onContinueLabel: undefined,
      onAccept: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });

  const normalizeAdminRows = (admins = []) => {
    return admins.map((admin, index) => ({
      id: admin._id || admin.id || `admin-${index}`,
      _id: admin._id || admin.id || `admin-${index}`,
      email: admin.email || "",
      password: admin.password || "",
      password_masked: maskPassword(admin.password),
      username: admin.username || "",
      enable: !!admin.enable,
      isSuperAdmin: !!admin.isSuperAdmin,
      createdAt: admin.createdAt || null,
      updatedAt: admin.updatedAt || null,
      source: admin.source || "mock",
    }));
  };

  const loadMockAdmins = useCallback(() => {
    const filtered = filterMockAdmins(mockAdmins, filterAdmin);
    setRowsAdmin(normalizeAdminRows(filtered));
  }, [mockAdmins, filterAdmin]);

  const RequestAdmins = useCallback(async () => {
    setLoading(true);

    try {
      let response = null;
      const hasBackendMethod =
        typeof adminMethods?.getAdmins === "function" ||
        typeof adminMethods?.adminsService === "function";

      if (hasBackendMethod) {
        if (typeof adminMethods.getAdmins === "function") {
          response = await adminMethods.getAdmins({
            search_filter: filterAdmin.search,
            enable_filter:
              filterAdmin.enable === ""
                ? ""
                : filterAdmin.enable === "true",
            super_admin_filter:
              filterAdmin.isSuperAdmin === ""
                ? ""
                : filterAdmin.isSuperAdmin === "true",
          });
        } else if (typeof adminMethods.adminsService === "function") {
          response = await adminMethods.adminsService({
            search_filter: filterAdmin.search,
            enable_filter:
              filterAdmin.enable === ""
                ? ""
                : filterAdmin.enable === "true",
            super_admin_filter:
              filterAdmin.isSuperAdmin === ""
                ? ""
                : filterAdmin.isSuperAdmin === "true",
          });
        }

        if (response?.status === 200) {
          const storedAdmins =
            response?.data?.data?.information_admin ||
            response?.data?.data?.admins ||
            response?.data?.data ||
            [];

          setRowsAdmin(normalizeAdminRows(storedAdmins));
          setLoading(false);
          return;
        }
      }

      loadMockAdmins();
    } catch (error) {
      console.error(error);
      setError("Failed to request admins");
      loadMockAdmins();
    }

    setLoading(false);
  }, [filterAdmin, loadMockAdmins]);

  useEffect(() => {
    RequestAdmins();
  }, [RequestAdmins]);

  const ADMIN_COLUMNS = useMemo(
    () => [
      { field: "email", headerName: "Email", flex: 2.2 },
      {
        field: "password_masked",
        headerName: "Password",
        flex: 1.4,
      },
      { field: "username", headerName: "Username", flex: 1.8 },
      {
        field: "enable",
        headerName: "Enable",
        flex: 1,
        renderCell: (params) => (
          <StatusChip value={params.value ? "Yes" : "No"} />
        ),
      },
      {
        field: "isSuperAdmin",
        headerName: "Is Super Admin",
        flex: 1.2,
        renderCell: (params) => (
          <StatusChip value={params.value ? "Yes" : "No"} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            sx={Styles.ActionsButton}
            onClick={(event) => handleOpenAdminMenu(event, params.row)}
          >
            <IconComponent icon="more_vert" size="12px" />
          </Button>
        ),
      },
    ],
    [handleOpenAdminMenu]
  );

  const getCreateAdminFields = () => [
    {
      name: "email",
      label: "Email",
      type: "email",
      value: "",
      placeholder: "Enter admin email",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      value: "",
      placeholder: "Enter password",
    },
    {
      name: "username",
      label: "Username",
      type: "text",
      value: "",
      placeholder: "Enter username",
    },
    {
      name: "enable",
      label: "Enable",
      type: "switch",
      value: true,
    },
    {
      name: "isSuperAdmin",
      label: "Is Super Admin",
      type: "switch",
      value: false,
    },
  ];

  const getEditAdminFields = (row) => [
    {
      name: "email",
      label: "Email",
      type: "email",
      value: row?.email || "",
      placeholder: "Enter admin email",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      value: "",
      placeholder: "Leave blank to keep current password",
      optional: true,
    },
    {
      name: "username",
      label: "Username",
      type: "text",
      value: row?.username || "",
      placeholder: "Enter username",
    },
    {
      name: "enable",
      label: "Enable",
      type: "switch",
      value: !!row?.enable,
    },
    {
      name: "isSuperAdmin",
      label: "Is Super Admin",
      type: "switch",
      value: !!row?.isSuperAdmin,
    },
  ];

  const handleCreateAdmin = () => {
    setFormModalConfig({
      open: true,
      title: "Register Admin",
      message: "",
      type: "form",
      mode: "admin",
      variant: "create",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      fields: getCreateAdminFields(),
      onSubmit: handleSubmitCreateAdmin,
      onClose: closeFormModal,
    });
  };

  const handleOpenEditAdminForm = (row) => {
    if (!row) return;

    setFormModalConfig({
      open: true,
      title: `Edit Admin ${row.username || ""}`.trim(),
      message: "",
      type: "form",
      mode: "admin",
      variant: "edit",
      confirmLabel: "Save Changes",
      cancelLabel: "Cancel",
      fields: getEditAdminFields(row),
      onSubmit: (formData) => handleSubmitEditAdmin(row, formData),
      onClose: closeFormModal,
    });
  };

  const handleEditAdmin = () => {
    const row = selectedAdminRow;
    handleCloseAdminMenu();
    if (!row) return;
    handleOpenEditAdminForm(row);
  };

  const handleSubmitCreateAdmin = async (formData) => {
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        username: formData.username,
        enable: !!formData.enable,
        isSuperAdmin: !!formData.isSuperAdmin,
      };

      if (typeof adminMethods?.createAdmin === "function") {
        const res = await adminMethods.createAdmin(payload);

        if (res.status === 200 || res.status === 201) {
          closeFormModal();
          openSuccessModal(() => RequestAdmins());
          return;
        }

        openErrorModal(res?.data?.message || "Failed to create admin");
        return;
      }

      const updatedMocks = createMockAdmin(mockAdmins, payload);
      setMockAdmins(updatedMocks);
      closeFormModal();
      openSuccessModal(() => {
        const filtered = filterMockAdmins(updatedMocks, filterAdmin);
        setRowsAdmin(normalizeAdminRows(filtered));
      });
    } catch (e) {
      console.error(e);
      openErrorModal(
        getEndpointMessage(e, "Failed to create admin. Please try again.")
      );
    }
  };

  const handleSubmitEditAdmin = async (row, formData) => {
    try {
      const payload = {
        id: row._id || row.id,
        email: formData.email,
        username: formData.username,
        enable: !!formData.enable,
        isSuperAdmin: !!formData.isSuperAdmin,
      };

      if (formData.password && String(formData.password).trim() !== "") {
        payload.password = formData.password;
      }

      if (typeof adminMethods?.editAdmin === "function") {
        const res = await adminMethods.editAdmin(payload);

        if (res.status === 200) {
          closeFormModal();
          openSuccessModal(() => RequestAdmins());
          return;
        }

        openErrorModal(res?.data?.message || "Failed to update admin");
        return;
      }

      const updatedMocks = updateMockAdmin(mockAdmins, row._id || row.id, payload);
      setMockAdmins(updatedMocks);
      closeFormModal();
      openSuccessModal(() => {
        const filtered = filterMockAdmins(updatedMocks, filterAdmin);
        setRowsAdmin(normalizeAdminRows(filtered));
      });
    } catch (e) {
      console.error(e);
      openErrorModal(getEndpointMessage(e, "Failed to update admin."));
    }
  };

  const handleMenuDeleteAdmin = () => {
    handleCloseAdminMenu();

    if (!selectedAdminRow) return;

    setModalConfig({
      open: true,
      iconType: "warning",
      title: "Are you sure you want to delete this admin?",
      message: "",
      onAcceptLabel: "Yes, delete",
      onRefuseLabel: "No, back",
      onContinueLabel: undefined,
      onAccept: () => handleDeleteAdmin(selectedAdminRow),
      onRefuse: undefined,
      onContinue: undefined,
      onClose: () =>
        setModalConfig((prev) => ({
          ...prev,
          open: false,
        })),
    });
  };

  const handleDeleteAdmin = async (row) => {
    try {
      const payload = {
        id: row._id || row.id,
        email: row.email,
      };

      if (typeof adminMethods?.deleteAdmin === "function") {
        const res = await adminMethods.deleteAdmin(payload);

        if (res.status === 200) {
          openSuccessModal(() => RequestAdmins());
          return;
        }

        openErrorModal(res?.data?.message || "Error deleting admin");
        return;
      }

      const updatedMocks = deleteMockAdmin(mockAdmins, row._id || row.id);
      setMockAdmins(updatedMocks);
      openSuccessModal(() => {
        const filtered = filterMockAdmins(updatedMocks, filterAdmin);
        setRowsAdmin(normalizeAdminRows(filtered));
      });
    } catch (e) {
      console.error(e);
      openErrorModal(
        getEndpointMessage(e, "Failed to delete admin. Please try again.")
      );
    }
  };

  return (
    <Paper elevation={2} sx={Styles.containerPaper}>
      <NotificationModal {...modalConfig} />

      <FormSystemModal {...formModalConfig} />

      <Typography variant="h5" gutterBottom>
        Admin Management
      </Typography>

      <Typography sx={{ fontSize: "15px", color: "#687B8E", mb: 2 }}>
        Take a look at all registered administrators.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: "#F8FAFC",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: 1.4,
              color: Colors.Secondary_700,
              marginTop: "-18px",
            }}
          >
            Search
          </Typography>

          <SearchBoxComponent
            value={filterAdmin.search}
            handleSearchTerm={(e) =>
              setFilterAdmin({ ...filterAdmin, search: e.target.value })
            }
            placeholderprops={"Email or Username"}
          />
        </Box>

        <SelectorSimple
          title="Enable"
          value={filterAdmin.enable}
          handleChange={(e) =>
            setFilterAdmin({ ...filterAdmin, enable: e.target.value })
          }
          options={ENABLE_OPTIONS}
          placeholder="Filter by status"
          error={false}
          helperText=""
          name="enable"
        />

        <SelectorSimple
          title="Role"
          value={filterAdmin.isSuperAdmin}
          handleChange={(e) =>
            setFilterAdmin({
              ...filterAdmin,
              isSuperAdmin: e.target.value,
            })
          }
          options={SUPER_ADMIN_OPTIONS}
          placeholder="Filter by role"
          error={false}
          helperText=""
          name="isSuperAdmin"
        />

        <Button3px
          label={"Search"}
          color={Colors.Warning_50}
          backgroundColor={Colors.Error_500}
          type="submit"
          HandleOnClick={() => RequestAdmins()}
        >
          Search
        </Button3px>

        <Box
          sx={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button onClick={handleCreateAdmin} sx={Styles.AddButton}>
            <IconComponent icon="add" size="16px" />
            <Typography
              sx={{
                fontFamily: "Plus Jakarta Sans, sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              Register Admin
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {loading ? (
          <Box sx={Styles.loading}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box sx={{ height: 600, width: "100%", marginTop: 0 }}>
            <TableFrontPagination
              rows={rowsAdmin || []}
              columns={ADMIN_COLUMNS}
            />
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchorElAdmin}
        open={adminMenuOpen}
        onClose={handleCloseAdminMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiMenuItem onClick={handleEditAdmin}>Edit Admin</MuiMenuItem>
        <MuiMenuItem onClick={handleMenuDeleteAdmin}>Delete Admin</MuiMenuItem>
      </Menu>
    </Paper>
  );
};

export default Admin_Management_Screen;